import { SagaIterator } from '@redux-saga/core';
import { AVAILABLE_TIME_SITE_STATUSES } from '@tempus/trials-types';
import { call, cancelled, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import api from '~/store/api';

import { GetTrialsBySiteRequest } from '../api/types';
import { getApiSiteId } from '../site';
import { SiteWithId } from '../site/types';
import { creators } from './actions';
import { GET_TRIALS, SET_TRIALS, GET_TRIAL_OPTIONS, GET_ALL_TRIALS, BaseTrial } from './types';
import { formatTrials, hasNonStatusFilters } from './utils';

export function* fetchTrials(data?: { params?: GetTrialsBySiteRequest }): SagaIterator {
  const selectedUserSite: SiteWithId | null = yield select(({ site }) => site.selectedUserSite);
  const siteId = getApiSiteId(selectedUserSite);
  const isPagedRequest = Boolean(data?.params?.pageSize);

  try {
    const { trials, count } = yield call(api.trialSitesSearch.getTrialsBySite, {
      filters: { statuses: AVAILABLE_TIME_SITE_STATUSES },
      pageSize: 0,
      ...data?.params,
      ...(siteId && { siteId }),
    });

    const currentSite: SiteWithId | null = yield select(({ site }) => site.selectedUserSite);
    if (getApiSiteId(currentSite) !== siteId) {
      if (!isPagedRequest) {
        yield* fetchTrials();
      }
      return;
    }

    const formattedData = formatTrials(trials);

    if (isPagedRequest) {
      const unfilteredCount: number = hasNonStatusFilters(data?.params?.filters)
        ? yield select(({ trial }) => trial.unfilteredCount)
        : count;

      yield put(creators.setTrials(formattedData, unfilteredCount, count, siteId));
    } else {
      yield put(creators.setAllTrials(formattedData));
    }
  } catch (error) {
    if (yield cancelled()) {
      return;
    }

    const currentSite: SiteWithId | null = yield select(({ site }) => site.selectedUserSite);
    if (getApiSiteId(currentSite) !== siteId) {
      if (!isPagedRequest) {
        yield* fetchTrials();
      }
      return;
    }

    const message = error instanceof Error ? error.message : 'Failed to fetch trials';
    yield put(isPagedRequest ? creators.getTrialsFailed(message, siteId) : creators.getAllTrialsFailed(message));
  }
}

function* fetchTrialsMeta(): SagaIterator {
  try {
    const latestApplicableAtLookup = yield call(api.documents.latestApplicableAtByTrial);
    yield put(creators.setTrialsMeta(latestApplicableAtLookup));
  } catch {
    // Do nothing. Not having the meta information doesn't hurt anything.
  }
}

function* fetchTrialOptions(): SagaIterator {
  const trialOptions: BaseTrial[] = yield select(({ trial }) => trial.trialOptions);

  if (trialOptions.length) {
    return;
  }

  try {
    const trials = yield call(api.trials.getAll);
    yield put(creators.setTrialOptions(trials));
  } catch (error) {
    if (error instanceof Error) {
      yield put(creators.getTrialOptionsFailed(error.message));
    }
  }
}

export default [
  takeLatest(GET_ALL_TRIALS, fetchTrials),
  takeLatest(GET_TRIALS, fetchTrials),
  takeLatest(GET_TRIAL_OPTIONS, fetchTrialOptions),
  takeLeading(SET_TRIALS, fetchTrialsMeta),
];
